// ============================================================
// Chess Arena — Board Module (Pure Logic + Rendering Helper)
// ============================================================

import {
  GridPos,
  TerrainType,
  LevelConfig,
  BOARD_COLS,
  BOARD_ROWS,
} from './types';

// --- Cell data stored per grid position ---

export interface CellData {
  pieceId: string | null;
  terrain: TerrainType | null;
}

// --- Board class ---

export class Board {
  private grid: CellData[][] = []; // grid[row][col]

  constructor() {
    this.clearGrid();
  }

  // ------------------------------------------------------------------
  // Initialisation
  // ------------------------------------------------------------------

  /** Reset grid to empty state. */
  clearGrid(): void {
    this.grid = [];
    for (let r = 0; r < BOARD_ROWS; r++) {
      const row: CellData[] = [];
      for (let c = 0; c < BOARD_COLS; c++) {
        row.push({ pieceId: null, terrain: null });
      }
      this.grid.push(row);
    }
  }

  /**
   * Initialise board (clear grid only).
   * Terrain is placed by TerrainSystem and synced back via setTerrain().
   */
  initBoard(_levelConfig: LevelConfig): void {
    this.clearGrid();
  }

  // ------------------------------------------------------------------
  // Queries
  // ------------------------------------------------------------------

  /** Check whether a position is within board bounds. */
  isValidPos(pos: GridPos): boolean {
    return pos.col >= 0 && pos.col < BOARD_COLS && pos.row >= 0 && pos.row < BOARD_ROWS;
  }

  /** Get cell data at a position (returns null if out of bounds). */
  getCellContent(pos: GridPos): CellData | null {
    if (!this.isValidPos(pos)) return null;
    return this.grid[pos.row][pos.col];
  }

  /** Check whether a cell is passable (not a rock, within bounds). */
  isPassable(pos: GridPos): boolean {
    if (!this.isValidPos(pos)) return false;
    return this.grid[pos.row][pos.col].terrain !== 'rock';
  }

  /** Check whether a cell is a bush. */
  isBush(pos: GridPos): boolean {
    if (!this.isValidPos(pos)) return false;
    return this.grid[pos.row][pos.col].terrain === 'bush';
  }

  /** Get the terrain grid as a 2D array (for AI snapshot). */
  getTerrainGrid(): (TerrainType | null)[][] {
    return this.grid.map(row => row.map(cell => cell.terrain));
  }

  /**
   * Get all empty cells (no piece, passable terrain) in an optional row range.
   * @param rowRange [startRow, endRow] inclusive. Defaults to entire board.
   */
  getEmptyCells(rowRange?: [number, number]): GridPos[] {
    const startRow = rowRange ? rowRange[0] : 0;
    const endRow = rowRange ? rowRange[1] : BOARD_ROWS - 1;
    const result: GridPos[] = [];
    for (let r = startRow; r <= endRow; r++) {
      for (let c = 0; c < BOARD_COLS; c++) {
        const cell = this.grid[r][c];
        if (cell.pieceId === null && cell.terrain !== 'rock' && cell.terrain !== 'bush') {
          result.push({ col: c, row: r });
        }
      }
    }
    return result;
  }

  // ------------------------------------------------------------------
  // Piece placement
  // ------------------------------------------------------------------

  /** Place a piece ID on the board. */
  placePiece(pieceId: string, pos: GridPos): void {
    if (!this.isValidPos(pos)) return;
    this.grid[pos.row][pos.col].pieceId = pieceId;
  }

  /** Remove piece from a cell (returns the removed piece ID or null). */
  removePiece(pos: GridPos): string | null {
    if (!this.isValidPos(pos)) return null;
    const id = this.grid[pos.row][pos.col].pieceId;
    this.grid[pos.row][pos.col].pieceId = null;
    return id;
  }

  /** Move a piece from one cell to another. Returns the piece ID moved, or null. */
  movePiece(from: GridPos, to: GridPos): string | null {
    if (!this.isValidPos(from) || !this.isValidPos(to)) return null;
    const id = this.grid[from.row][from.col].pieceId;
    if (id === null) return null;
    this.grid[from.row][from.col].pieceId = null;
    this.grid[to.row][to.col].pieceId = id;
    return id;
  }

  /** Set terrain on a specific cell (used for manual placement / testing). */
  setTerrain(pos: GridPos, terrain: TerrainType | null): void {
    if (!this.isValidPos(pos)) return;
    this.grid[pos.row][pos.col].terrain = terrain;
  }

  // ------------------------------------------------------------------
  // Coordinate conversion
  // ------------------------------------------------------------------

  /** Convert grid position to pixel centre of that cell. */
  static gridToPixel(
    pos: GridPos,
    cellSize: number,
    offsetX: number,
    offsetY: number,
    cellH?: number,
  ): { x: number; y: number } {
    const ch = cellH ?? cellSize;
    return {
      x: offsetX + pos.col * cellSize + cellSize / 2,
      y: offsetY + pos.row * ch + ch / 2,
    };
  }

  /** Convert pixel position to grid position (floored). Returns null if out of bounds. */
  static pixelToGrid(
    x: number,
    y: number,
    cellSize: number,
    offsetX: number,
    offsetY: number,
    cellH?: number,
  ): GridPos | null {
    const ch = cellH ?? cellSize;
    const col = Math.floor((x - offsetX) / cellSize);
    const row = Math.floor((y - offsetY) / ch);
    if (col < 0 || col >= BOARD_COLS || row < 0 || row >= BOARD_ROWS) return null;
    return { col, row };
  }
}
